import { fmt, fmtDate } from '@dcc-erp/shared';
import type { BankStatementLineStatus, BankStatementWithChildren } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { num } from './recon-helpers';

const LINE_BADGE: Readonly<Record<BankStatementLineStatus, { l: string; c: string }>> = {
  OPEN: { l: 'รอจับคู่', c: 'warning' },
  MATCHED: { l: 'จับคู่แล้ว', c: 'positive' },
  ADJUSTED: { l: 'ปรับปรุงแล้ว', c: 'info' },
};

interface Props {
  statement: BankStatementWithChildren | undefined;
  bankLabel: string;
  onAutoMatch: () => void;
  onAddAdjustment: () => void;
  autoPending: boolean;
}

export function MatchWorkspace({
  statement,
  bankLabel,
  onAutoMatch,
  onAddAdjustment,
  autoPending,
}: Props) {
  if (!statement) {
    return (
      <div className="card">
        <div className="card-body muted" style={{ padding: 40, textAlign: 'center' }}>
          ยังไม่มี statement สำหรับบัญชีนี้ — กดปุ่ม "นำเข้า Bank Statement" ด้านบนเพื่อเริ่ม
        </div>
      </div>
    );
  }
  const matched = statement.lines.filter((l) => l.status === 'MATCHED').length;
  const open = statement.lines.filter((l) => l.status === 'OPEN').length;
  const adjusted = statement.lines.filter((l) => l.status === 'ADJUSTED').length;

  return (
    <>
      <div className="card" style={{ marginBottom: 'var(--section-gap)' }}>
        <div className="card-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <b>{bankLabel}</b>
            <span className="badge brand">งวด {fmtDate(statement.statementDate)}</span>
            <span className="meta">
              จับคู่แล้ว {matched} · ปรับปรุง {adjusted} · ค้าง {open}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn sm" onClick={onAutoMatch} disabled={autoPending}>
              <Icon name="link" size={12} /> Auto-match
            </button>
            <button className="btn sm" onClick={onAddAdjustment}>
              + เพิ่มรายการปรับปรุง
            </button>
            <button className="btn sm" disabled title="เฟสถัดไป">
              จับคู่เอง (Manual Match)
            </button>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--section-gap)' }}>
        <div className="card" style={{ opacity: 0.6 }}>
          <div className="card-head" style={{ background: 'var(--brand-tint)' }}>
            <h3 style={{ color: 'var(--brand)' }}>📒 จับคู่เอง (Book Entries)</h3>
          </div>
          <div className="card-body" style={{ padding: 40, textAlign: 'center' }}>
            <span className="muted">
              การจับคู่เอง (เลือกใบสำคัญบัญชี ↔ รายการ statement) — เฟสถัดไป
            </span>
          </div>
        </div>

        <div className="card">
          <div className="card-head" style={{ background: '#e8eff8' }}>
            <div>
              <h3 style={{ color: 'var(--info)' }}>
                🏦 ใน Bank Statement — {statement.lines.length} รายการ
              </h3>
              <div className="sub">
                {statement.bankCode} {statement.bankAccNo}
              </div>
            </div>
          </div>
          <div className="card-body flush">
            <div style={{ maxHeight: 500, overflow: 'auto' }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>วันที่</th>
                    <th>รายการในธนาคาร</th>
                    <th>ref</th>
                    <th className="num">จำนวน</th>
                    <th>สถานะ</th>
                  </tr>
                </thead>
                <tbody>
                  {statement.lines.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="muted"
                        style={{ padding: 32, textAlign: 'center' }}
                      >
                        ยังไม่มีรายการ — นำเข้า statement ก่อน
                      </td>
                    </tr>
                  )}
                  {statement.lines.map((l) => {
                    const amt = num(l.credit) - num(l.debit);
                    const badge = LINE_BADGE[l.status];
                    return (
                      <tr key={l.id}>
                        <td>{fmtDate(l.lineDate)}</td>
                        <td className="muted">{l.description}</td>
                        <td className="code muted">{l.reference ?? '—'}</td>
                        <td className={`num tnum ${amt < 0 ? 'neg' : 'pos'}`}>{fmt(amt)}</td>
                        <td>
                          <span className={`badge ${badge.c}`} style={{ fontSize: 13 }}>
                            {badge.l}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
